/*****************************************************************************/
/*  File Name: itemSelect.js                                                 */
/*  Date:      June 22, 2023                                                 */
/*  Author:    Christopher Cressman                                          */
/*  Purpose:   This will handle the item selections of the systems on the
/*             bottom half of the bar
/*
/*****************************************************************************/

import React /* useState,*/ from 'react';
import { withTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import styles from './styles/roi.module.scss';
import { MenuItem, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { useROIContext } from '../../context/roiContext';
import Checkbox from '@mui/material/Checkbox';
import { convertToCurrency } from '../../functions/convertCurrency';
import { useTipContext } from '../../context/tipContext';
import { convertToNumber } from '../../functions/convertNumber';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SoltaSelect from './elements/SelectField';
import TipPriceTextField from './elements/TipPriceField';
import * as TIMERANGE from '../../constants/timeRanges';
import * as MODES from '../../constants/modes';

function TipSelect({ t }) {
  //Load all of the default component varaibles

  const { selectedTips, setSelectedTips, consumablePrice, addAnotherTreatment, removeTreatment, selectedTimeRange, addTipCount, removeTipCount, roiMode, consumablePriceSingle } = useROIContext();

  const { tips, tipsLoading } = useTipContext();

  //console.log('this is my selected tips ----> ', selectedTips);
  //console.log("all tips -> ", tips);
  const checkBoxLabel = { inputProps: { 'aria-label': 'Combination Tip' } }

  return (
    <Grid item xs={12} container className={`${styles.tipSelectGrid} ${styles.mobileTreatmentCards}`}>
      <Grid item xs={12} container className={styles.tipHeadline} justifyContent='space-between'>
        <Grid item className={styles.title}>
          {roiMode === MODES.consumer ? 'Current' : null} Treatments
        </Grid>
        <Grid item>
          <Button className={styles.addTreatmentButton} onClick={() => addAnotherTreatment()} startIcon={<AddCircleOutlineIcon />}>
            Add another treatment
          </Button>
        </Grid>
      </Grid>

      <Grid item xs={12} container alignItems='flex-end' className={`${styles.tipLine} ${styles.headline}`}>
        <Grid item className={styles.removeGrid}>
          {/* Remove button place holder*/}
        </Grid>
        <Grid item xs className={styles.tipSelectDropDown}>
          Treatment Tip
        </Grid>
        <Grid item xs >
          Combination Tip
        </Grid>
        <Grid item xs className={styles.quantityGrid}>
          Treatments Per {selectedTimeRange === TIMERANGE.weekly ? 'Week' : 'Month'}
        </Grid>
        <Grid item xs className={`${styles.tipPriceGrid} `}>
          Treatment Price to patient
        </Grid>
        <Grid item className={`${styles.tipPriceGrid} `}>
          Tip Price Per Treatment
        </Grid>
        <Grid item xs className={`${styles.tipPriceGrid}`}>
          Other Charges
        </Grid>
      </Grid>
      <Grid item xs={12} container className={styles.tipGroup}>
        {!tipsLoading
          ? selectedTips.map((myTip, i) => (
            <Grid item xs={12} container key={i} alignItems='center' className={styles.tipLine}>
              <Grid item className={styles.removeGrid}>
                <Tooltip title='Remove Item?'>
                  <IconButton onClick={() => removeTreatment(i)} className={styles.removeButton}>
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs className={styles.tipSelectDropDown} data-label='Treatment tip'>
                <SoltaSelect
                  labelId='tip-select'
                  id='tip-select'
                  value={selectedTips[i].id}
                  onChange={(event) =>
                    setSelectedTips((prevSelectedTips) => {
                      const updatedSelectedTips = [...prevSelectedTips]; // Create a new array with the same values as prevSelectedTips

                      //Get all of the tips information
                      const selectedTip = tips.filter((tip) => tip.id === event.target.value).map((tip) => tip);
                      // Update the second entry's sku value
                      updatedSelectedTips[i] = selectedTip[0];
                      updatedSelectedTips[i].amount = 1; //Set the default quantiy to 1

                      return updatedSelectedTips; // Return the updated array as the new state
                    })
                  }
                >
                  {tips.map((tip) => (
                    <MenuItem value={tip.id} key={tip.id}>
                      <Typography className={styles.tipName}>{tip.sku}</Typography>
                    </MenuItem>
                  ))}
                </SoltaSelect>
              </Grid>
              <Grid item xs container alignItems='center' justifyContent='center' alignContent='center' data-label='Combination tip'>
                <Checkbox {...checkBoxLabel}
                  disabled={roiMode === MODES.consumer && !myTip.id}
                  checked={selectedTips[i].combinationTip ?? false}
                  onChange={(event) =>
                    setSelectedTips((prevSelectedTips) => {
                      const updatedSelectedTips = [...prevSelectedTips];
                      updatedSelectedTips[i].combinationTip = event.target.checked;
                      return updatedSelectedTips;
                    })
                  }

                  sx={{
                    color: '#5a9bab',
                    '&.Mui-checked': {
                      color: '#5a9bab',
                    },
                  }} />
              </Grid>
              <Grid item xs container alignItems='center' justifyContent='center' className={styles.quantityGrid} data-label={`Treatments per ${selectedTimeRange === TIMERANGE.weekly ? 'week' : 'month'}`}>
                <Grid item xs className={styles.quantityButtons} alignSelf='center'>
                  <Tooltip title='Subtract Quantity'>
                    <span>
                      <IconButton disabled={convertToNumber(myTip.amount ?? 0) <= 1} className={styles.quantityButton} onClick={() => removeTipCount(i)}>
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
                <Grid item xs className={styles.quantityField} alignSelf='center'>
                  <TipPriceTextField
                    disabled={roiMode === MODES.consumer && !myTip.id}
                    value={convertToNumber(myTip.amount ?? 0)}
                    onChange={(event) =>
                      setSelectedTips((prevSelectedTips) => {
                        const updatedSelectedTips = [...prevSelectedTips];
                        updatedSelectedTips[i].amount = convertToNumber(event.target.value);
                        return updatedSelectedTips;
                      })
                    }
                  />
                </Grid>
                <Grid item xs className={styles.quantityButtons}>
                  <Tooltip title='Add Quantity'>
                    <span>
                      <IconButton disabled={!myTip.id} className={styles.quantityButton} onClick={() => addTipCount(i)}>
                        <AddCircleOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
              <Grid item xs className={`${styles.tipPriceGrid} ${styles.price}`} data-label='Treatment price to patient'>
                <TipPriceTextField
                  disabled={roiMode === MODES.consumer && !myTip.id}
                  value={convertToCurrency(myTip.treatmentPrice) ?? 0}
                  onChange={(event) =>
                    setSelectedTips((prevSelectedTips) => {
                      const updatedSelectedTips = prevSelectedTips.map((tip) => ({ ...tip }));
                      updatedSelectedTips[i].treatmentPrice = convertToNumber(event.target.value);
                      return updatedSelectedTips;
                    })
                  }
                />
                {/* ${myTip.treatmentPrice ? convertToCurrency(myTip.treatmentPrice) : 0} */}
              </Grid>
              <Grid item className={`${styles.tipPriceGrid} ${styles.price}`} data-label='Tip price per treatment'>
                ${convertToCurrency(consumablePriceSingle(i))}
              </Grid>
              <Grid item xs className={`${styles.tipPriceGrid}`} data-label='Other charges'>
                <TipPriceTextField
                  disabled={roiMode === MODES.consumer && !myTip.id}
                  value={myTip.other ?? 0}
                  onChange={(event) =>
                    setSelectedTips((prevSelectedTips) => {
                      const updatedSelectedTips = [...prevSelectedTips];
                      updatedSelectedTips[i].other = convertToNumber(event.target.value);
                      return updatedSelectedTips;
                    })
                  }
                />
              </Grid>
            </Grid>
          ))
          : 'Please select a machine'}
      </Grid>
    </Grid>
  );
}

export default withTranslation()(TipSelect);
